import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  message,
  Typography,
  Alert,
  Space,
  Tag,
  Divider,
  Row,
  Col,
  InputNumber,
} from 'antd';
import { SaveOutlined, SendOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

interface WebhookConfig {
  webhook_url: string;
  enabled: boolean;
  thresholds: number[];
  secret: string;
}

const WebhookConfigPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchWebhookConfig();
  }, []);

  const fetchWebhookConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/super-admin/webhook-config');
      const config = response.data.webhook_config || {};
      
      form.setFieldsValue({
        webhook_url: config.webhook_url || '',
        enabled: config.enabled || config.webhook_enabled || false,
        threshold_75: config.thresholds?.includes(75) || false,
        threshold_50: config.thresholds?.includes(50) || false,
        threshold_25: config.thresholds?.includes(25) || false,
        threshold_5: config.thresholds?.includes(5) || false,
        threshold_0: config.thresholds?.includes(0) || false,
      });
    } catch (error: any) {
      console.error('Webhook config error:', error);
      message.error(error.response?.data?.message || 'Failed to load webhook configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Build thresholds array from checkboxes
      const thresholds: number[] = [];
      if (values.threshold_75) thresholds.push(75);
      if (values.threshold_50) thresholds.push(50);
      if (values.threshold_25) thresholds.push(25);
      if (values.threshold_5) thresholds.push(5);
      if (values.threshold_0) thresholds.push(0);
      
      const payload = {
        webhook_url: values.webhook_url,
        enabled: values.enabled,
        thresholds,
      };
      
      await api.put('/api/super-admin/webhook-config', payload);
      message.success('Webhook configuration saved successfully');
      fetchWebhookConfig();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.response?.data?.message || 'Failed to save configuration');
    }
  };

  const handleTestWebhook = async () => {
    try {
      setTestingWebhook(true);
      const response = await api.post('/api/super-admin/webhook-config/test');
      
      if (response.data.success) {
        message.success(`Test webhook sent successfully! Status: ${response.data.status_code}`);
      } else {
        message.error(`Test webhook failed: ${response.data.message}`);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to send test webhook');
    } finally {
      setTestingWebhook(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Webhook Configuration</Title>

      <Alert
        message="Credit Threshold Alerts"
        description="Configure webhooks to receive notifications when tenant credit balances reach specified thresholds. Each threshold triggers only once until credits are topped up."
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="webhook_url"
            label="Webhook URL"
            rules={[
              { required: true, message: 'Please enter webhook URL' },
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input
              placeholder="https://your-erp.com/api/webhooks/credit-alerts"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="enabled"
            label="Webhook Status"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Enabled"
              unCheckedChildren="Disabled"
            />
          </Form.Item>

          <Divider>Alert Thresholds</Divider>

          <Alert
            message="Threshold Behavior"
            description="Select which credit percentage thresholds should trigger webhooks. Alerts fire once per threshold and reset after credit top-up."
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Form.Item name="threshold_75" valuePropName="checked" noStyle>
              <Card size="small" style={{ backgroundColor: '#e6f7ff' }}>
                <Row align="middle">
                  <Col span={20}>
                    <Space>
                      <Tag color="blue">75%</Tag>
                      <Text>Alert when balance drops to 75% of plan credits</Text>
                    </Space>
                  </Col>
                  <Col span={4} style={{ textAlign: 'right' }}>
                    <Form.Item name="threshold_75" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Form.Item>

            <Form.Item name="threshold_50" valuePropName="checked" noStyle>
              <Card size="small" style={{ backgroundColor: '#fffbe6' }}>
                <Row align="middle">
                  <Col span={20}>
                    <Space>
                      <Tag color="yellow">50%</Tag>
                      <Text>Alert when balance drops to 50% of plan credits</Text>
                    </Space>
                  </Col>
                  <Col span={4} style={{ textAlign: 'right' }}>
                    <Form.Item name="threshold_50" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Form.Item>

            <Form.Item name="threshold_25" valuePropName="checked" noStyle>
              <Card size="small" style={{ backgroundColor: '#fff7e6' }}>
                <Row align="middle">
                  <Col span={20}>
                    <Space>
                      <Tag color="orange">25%</Tag>
                      <Text>Alert when balance drops to 25% of plan credits</Text>
                    </Space>
                  </Col>
                  <Col span={4} style={{ textAlign: 'right' }}>
                    <Form.Item name="threshold_25" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Form.Item>

            <Form.Item name="threshold_5" valuePropName="checked" noStyle>
              <Card size="small" style={{ backgroundColor: '#fff1f0' }}>
                <Row align="middle">
                  <Col span={20}>
                    <Space>
                      <Tag color="red">5%</Tag>
                      <Text strong>Alert when balance drops to 5% - Critical!</Text>
                    </Space>
                  </Col>
                  <Col span={4} style={{ textAlign: 'right' }}>
                    <Form.Item name="threshold_5" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Form.Item>

            <Form.Item name="threshold_0" valuePropName="checked" noStyle>
              <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
                <Row align="middle">
                  <Col span={20}>
                    <Space>
                      <Tag color="default">0%</Tag>
                      <Text type="danger" strong>Alert when balance reaches zero - Service stopped!</Text>
                    </Space>
                  </Col>
                  <Col span={4} style={{ textAlign: 'right' }}>
                    <Form.Item name="threshold_0" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Form.Item>
          </Space>

          <Divider />

          <Space size="middle">
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              size="large"
            >
              Save Configuration
            </Button>
            <Button
              icon={<SendOutlined />}
              onClick={handleTestWebhook}
              loading={testingWebhook}
              size="large"
            >
              Send Test Webhook
            </Button>
          </Space>
        </Form>
      </Card>

      <Card title="Webhook Payload Format" style={{ marginTop: '24px' }}>
        <Paragraph>
          When a threshold is reached, we'll send a POST request to your webhook URL with the following JSON payload:
        </Paragraph>
        <Paragraph>
          <pre style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px' }}>
{`{
  "event_type": "credit_threshold_alert",
  "tenant_id": "client_abc123",
  "tenant_name": "Acme Corporation",
  "current_balance": 250,
  "threshold_percentage": 25,
  "plan_code": "PROFESSIONAL",
  "alert_timestamp": "2026-02-03T10:30:00Z",
  "signature": "hmac_sha256_signature_here"
}`}
          </pre>
        </Paragraph>
        <Alert
          message="Signature Verification"
          description="The 'signature' field contains an HMAC SHA-256 signature for verifying the webhook authenticity. Use the secret key from your configuration to verify requests."
          type="warning"
          showIcon
        />
      </Card>
    </div>
  );
};

export default WebhookConfigPage;
